import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import withRouter from '../../components/Common/withRouter';
import { withTranslation } from 'react-i18next';

// Formik validation
import * as Yup from 'yup';
import { useFormik } from 'formik';
import api from '../../helpers/api';
import { applyLoginSuccess } from '../../helpers/authSession';

// import images
import logo from '../../assets/images/white logo png.png';

// Auth proceeds email-first: enter email → backend SSO discovery decides whether
// to redirect to the customer IdP or fall through to the password field.
const STEP_EMAIL = 'EMAIL';
const STEP_PASSWORD = 'PASSWORD';
const STEP_ORG = 'ORG';

const Login = (props) => {
  const { t, i18n } = props;
  document.title = t('shared.login', 'Login') + ' | Arcmedix';

  const location = useLocation();
  // location.state.error is set when the SSO callback bounces a failed handshake back here.
  const [error, setError] = useState(location.state?.error || null);
  const [success] = useState(location.state?.message || null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEP_EMAIL);
  const [orgOptions, setOrgOptions] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (location.state?.message || location.state?.error) {
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Calls SSO discovery for the entered email. When the second call is needed
  // (shared domain across orgs), pass the chosen organizationId.
  const runEmailLookup = async (email, organizationId = null) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('auth/sso/email-lookup', {
        email,
        organizationId,
        deviceType: 'WEB',
      });
      const result = data.response || {};

      if (result.ssoAvailable && result.needsOrgSelection) {
        // Shared domain → let the user pick which org to authenticate against.
        setOrgOptions(Array.isArray(result.organizations) ? result.organizations : []);
        setStep(STEP_ORG);
        return;
      }
      if (result.ssoAvailable && result.authorizationUrl) {
        // Hand off to the IdP (full-page redirect; returns to /auth/sso/callback).
        window.location.assign(result.authorizationUrl);
        return;
      }
      // No SSO for this email → reveal the password field.
      setStep(STEP_PASSWORD);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t('login.an_error_occurred_during_login', 'An error occurred during login')
      );
    } finally {
      setLoading(false);
    }
  };

  // Password login (non-SSO users / break-glass).
  const runPasswordLogin = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('auth/login', { identifier: email, password });
      if (response.status === 200) {
        applyLoginSuccess({
          responseData: response.data.response,
          navigate,
          i18n,
          t,
          onError: setError,
          dispatch,
        });
      } else {
        setError(response.message || t('login.login_failed', 'Login failed'));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('login.invalid_credentials', 'Invalid credentials'));
      } else if (err.response?.status === 403) {
        setError(t('login.unauthorized_users_only', 'Unauthorized: Only authorized users can access this system'));
      } else {
        setError(
          err.response?.data?.message || err.message || t('login.an_error_occurred_during_login', 'An error occurred during login')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t('shared.invalid_email', 'Please enter a valid email address'))
        .required(t('shared.email_is_required', 'Email is required')),
      password:
        step === STEP_PASSWORD
          ? Yup.string().required(t('shared.please_enter_password', 'Please Enter Your Password'))
          : Yup.string(),
    }),
    onSubmit: async (values) => {
      if (step === STEP_PASSWORD) {
        await runPasswordLogin(values.email, values.password);
      } else {
        await runEmailLookup(values.email);
      }
    },
  });

  // Returns to the email step so the user can correct/replace the email.
  const resetToEmail = () => {
    setStep(STEP_EMAIL);
    setOrgOptions([]);
    setError(null);
    validation.setFieldValue('password', '');
  };

  return (
    <React.Fragment>
      <div className="authentication-bg min-vh-100">
        <div className="bg-overlay"></div>
        <Container>
          <div className="d-flex flex-column min-vh-100 px-3">
            <Row className="justify-content-center my-auto">
              <Col md={8} lg={6} xl={5}>
                <div className="text-center">
                  <Link to="/">
                    <img src={logo} alt="" height="90" width="200" />
                  </Link>
                </div>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-center">
                      <h5 className="text-primary">{t('shared.welcome_back', 'Welcome Back !')}</h5>
                      <p className="text-muted">
                        {t('shared.sign_in_to_continue', 'Sign in to continue to Arcmedix')}
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      {success && (
                        <Alert color="success">
                          <div className="d-flex align-items-center">
                            <i className="bx bx-check-circle me-2"></i>
                            <div>{success}</div>
                          </div>
                        </Alert>
                      )}
                      {error ? (
                        <Alert color="danger">
                          <div className="d-flex align-items-center">
                            <i className="bx bx-error-circle me-2"></i>
                            <div>{error}</div>
                          </div>
                        </Alert>
                      ) : null}

                      {step === STEP_ORG ? (
                        <div>
                          <Label className="form-label">
                            {t('sso.select_organization_prompt', 'Select your organization to continue')}
                          </Label>
                          <ListGroup className="mb-3">
                            {orgOptions.map((org) => (
                              <ListGroupItem
                                key={org.organizationId}
                                tag="button"
                                action
                                type="button"
                                disabled={loading}
                                onClick={() => runEmailLookup(validation.values.email, org.organizationId)}
                              >
                                {org.organizationName}
                              </ListGroupItem>
                            ))}
                          </ListGroup>
                          <div className="text-center">
                            <button type="button" className="btn btn-link p-0" onClick={resetToEmail}>
                              {t('sso.use_different_email', 'Use a different email')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Form
                          className="form-horizontal"
                          onSubmit={(e) => {
                            e.preventDefault();
                            validation.handleSubmit();
                            return false;
                          }}
                        >
                          <div className="mb-3">
                            <Label className="form-label">{t('shared.email', 'Email')}</Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder={t('shared.enter_email', 'Enter email')}
                              type="email"
                              autoComplete="username"
                              disabled={step === STEP_PASSWORD}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ''}
                              invalid={
                                validation.touched.email && validation.errors.email ? true : false
                              }
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid">
                                <div>{validation.errors.email}</div>
                              </FormFeedback>
                            ) : null}
                          </div>

                          {step === STEP_PASSWORD && (
                            <div className="mb-3">
                              <Label className="form-label">{t('shared.password', 'Password')}</Label>
                              <Input
                                name="password"
                                value={validation.values.password || ''}
                                type="password"
                                autoComplete="current-password"
                                placeholder={t('shared.enter_password', 'Enter Password')}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                  validation.touched.password && validation.errors.password ? true : false
                                }
                              />
                              {validation.touched.password && validation.errors.password ? (
                                <FormFeedback type="invalid">
                                  <div>{validation.errors.password}</div>
                                </FormFeedback>
                              ) : null}
                              <div className="mt-2 text-end">
                                <Link to="/forgot-password" className="text-muted text-decoration-underline">
                                  {t('login.forgot_password', 'Forgot Password?')}
                                </Link>
                              </div>
                            </div>
                          )}

                          <div className="mt-3 text-center">
                            <button
                              className="btn btn-primary w-sm waves-effect waves-light"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {step === STEP_PASSWORD
                                    ? t('shared.logging_in', 'Logging in...')
                                    : t('shared.loading', 'Loading...')}
                                </>
                              ) : step === STEP_PASSWORD ? (
                                t('shared.log_in', 'Log In')
                              ) : (
                                t('shared.continue', 'Continue')
                              )}
                            </button>
                          </div>

                          {step === STEP_PASSWORD && (
                            <div className="mt-3 text-center">
                              <button type="button" className="btn btn-link p-0" onClick={resetToEmail}>
                                {t('sso.use_different_email', 'Use a different email')}
                              </button>
                            </div>
                          )}
                        </Form>
                      )}

                      <div className="mt-3 text-center">
                        <p className="mb-0">
                          {t('login.no_account', "Don't have an account?")}{' '}
                          <Link to="/register" className="text-primary fw-semibold">
                            {t('login.register', 'Register')}
                          </Link>
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted p-4">
                  <p className="text-white-50">
                    {t('shared.copyright', 'Copyright ©')} {new Date().getFullYear()} {t('shared.arcmedix_all_rights_reserved', 'Arcmedix. All rights reserved')}
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(withTranslation()(Login));

Login.propTypes = {
  history: PropTypes.object,
  t: PropTypes.func,
  i18n: PropTypes.object,
};
